import { useContext, useState } from 'react';
import { useDrawerStore } from 'store/drawer.store.ts';
import { useGetMemoryConnectTokenMutation } from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { paths } from '@types';
import { useMediaQuery } from '@mui/material';
import { PatientContext } from '@views/pages/Patient/context';

export const useSummary = (
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const { setDrawerOpen, setTaskId } = useDrawerStore(state => state);
  const [memoryConnectToken, setMemoryConnectToken] = useState<string | null>(
    null,
  );
  const [selectedProgram, setSelectedProgram] = useState<
    | paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json']['programs'][0]
    | null
  >(null);

  const { handleTriggerTabChange } = useContext(PatientContext);

  const isLgSizeUp = useMediaQuery('(min-width:1536px)');
  const isMdSizeUp = useMediaQuery('(min-width:1200px)');

  const programColumnsLimit = isLgSizeUp ? 4 : isMdSizeUp ? 2 : 2;

  const handleCloseSelectedProgramView = () => {
    setSelectedProgram(null);
  };

  const handleOpenProgramView = (
    program: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json']['programs'][0],
  ) => {
    setSelectedProgram(program);
  };

  const { mutateAsync, isPending: isTokenGenerating } =
    useGetMemoryConnectTokenMutation(patient.id);

  const handleOpenTaskDetails = (id: string) => {
    setDrawerOpen(true);
    setTaskId(id);
  };

  const handleGenerateMemoryConnectToken = async () => {
    try {
      const {
        data: { token },
      } = await mutateAsync();

      if (token) setMemoryConnectToken(token);

      toast.success('We generated a token for you!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCopyToClipboard = (text: string) => {
    toast.success('Token is copied to a clipboard!');
    navigator.clipboard.writeText(text);
  };

  const handleNavigateToAllTasks = () => {
    handleTriggerTabChange(5);
  };

  return {
    handleOpenTaskDetails,
    isTokenGenerating,
    memoryConnectToken,
    handleGenerateMemoryConnectToken,
    handleCopyToClipboard,
    showMemoryConnect: true,
    handleNavigateToAllTasks,
    selectedProgram,
    handleOpenProgramView,
    handleCloseSelectedProgramView,
    programColumnsLimit,
  };
};
